/**
 * Ads Summary Card - Compact View for Marketing Tracker
 * 
 * Shows a condensed view of ads performance with:
 * - Key KPIs
 * - Top campaigns table (limited)
 * - Link to full Ads Manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DollarSign,
  Users,
  TrendingUp,
  ExternalLink,
  RefreshCw,
  Loader2,
  AlertCircle,
  Target,
  BarChart3,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('en-US');
};

// Calculate date range
const getDateRange = (rangeValue) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start, end;
  
  switch (rangeValue) {
    case 'today':
      start = end = today;
      break;
    case 'yesterday':
      start = end = new Date(today.getTime() - 86400000);
      break;
    case 'last_7':
      start = new Date(today.getTime() - 7 * 86400000);
      end = today;
      break;
    case 'last_14':
      start = new Date(today.getTime() - 14 * 86400000);
      end = today;
      break;
    case 'last_30':
      start = new Date(today.getTime() - 30 * 86400000);
      end = today;
      break;
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = today;
      break;
    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'ytd':
      start = new Date(now.getFullYear(), 0, 1);
      end = today;
      break;
    default:
      start = new Date(today.getTime() - 30 * 86400000);
      end = today;
  }
  
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

// Platform badge config
const PLATFORM_CONFIG = {
  meta: { label: 'Meta', icon: '📘' },
  google: { label: 'Google', icon: '🔍' },
};

const AdsSummaryCard = ({ dateRange = 'last_30' }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, platforms: [] });

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const connections = response.data || [];
      const connectedPlatforms = connections.filter(c => c.status === 'active');
      setConnectionStatus({
        connected: connectedPlatforms.length > 0,
        platforms: connectedPlatforms.map(c => c.platform)
      });
    } catch (err) {
      console.log('Could not fetch connection status');
      setConnectionStatus({ connected: false, platforms: [] });
    }
  }, [token]);

  // Fetch ads data - tries real data first
  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const dates = getDateRange(dateRange);
      
      // First try real data
      try {
        const response = await axios.post(
          `${API}/api/marketing/ads/report`,
          {
            level: 'campaign',
            start_date: dates.start,
            end_date: dates.end,
            sort_by: 'spend',
            sort_order: 'desc',
            page: 1,
            page_size: 5,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setData(response.data.data || []);
        setTotals(response.data.totals || {});
        setLastSync(new Date().toISOString());
        return;
      } catch (realDataErr) {
        console.log('Real ads data not available');
      }
      
      // Fallback to demo
      const response = await axios.get(
        `${API}/api/marketing/ads/report/demo`,
        {
          params: {
            level: 'campaign',
            start_date: dates.start,
            end_date: dates.end,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Get top 5 campaigns by spend
      const sortedData = (response.data.data || [])
        .sort((a, b) => (b.spend || 0) - (a.spend || 0))
        .slice(0, 5);
      
      setData(sortedData);
      setTotals(response.data.totals || {});
      setLastSync(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching ads summary:', err);
      const errorDetail = err.response?.data?.detail;
      if (errorDetail?.includes('Demo mode is disabled') || errorDetail?.includes('no real data')) {
        setError('Connect ad platforms to see data');
      } else {
        setError('Failed to load ads data');
      }
      setData([]);
      setTotals({});
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchData();
    fetchConnectionStatus();
  }, [fetchData, fetchConnectionStatus]);

  // Navigate to full Ads Manager
  const openAdsManager = () => {
    navigate('/marketing-hub/ads-manager');
  };

  // Determine badge based on actual connection status
  const renderConnectionBadge = () => {
    if (connectionStatus.connected) {
      return (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
          <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
          {connectionStatus.platforms.length} Connected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50">
        <AlertCircle className="h-3 w-3 mr-1 text-amber-500" />
        Not Connected
      </Badge>
    );
  };

  return (
    <Card className="overflow-hidden" data-testid="ads-summary-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Ads Performance</CardTitle>
            {renderConnectionBadge()}
          </div>
          <div className="flex items-center gap-2">
            {lastSync && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="h-7 w-7 p-0"
              data-testid="refresh-ads-summary"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openAdsManager}
              className="h-7 text-xs gap-1"
              data-testid="open-ads-manager"
            >
              View All
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="h-40 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <AlertCircle className="h-6 w-6" />
            <span className="text-sm">{error}</span>
            <Button variant="outline" size="sm" onClick={openAdsManager}>
              Connect Platforms
            </Button>
          </div>
        ) : !connectionStatus.connected && (!totals.spend && !totals.leads) ? (
          <div className="h-40 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <BarChart3 className="h-6 w-6" />
            <span className="text-sm">Connect ad platforms to see performance data</span>
            <Button variant="outline" size="sm" onClick={openAdsManager}>
              Connect Platforms
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* KPI Row */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-2 rounded-lg bg-muted/50">
                <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Total Spend
                </div>
                <div className="text-lg font-bold">{totals.spend ? formatCurrency(totals.spend) : '-'}</div>
              </div>
              <div className="p-2 rounded-lg bg-muted/50">
                <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Leads
                </div>
                <div className="text-lg font-bold">{totals.leads ? formatNumber(totals.leads) : '-'}</div>
              </div>
              <div className="p-2 rounded-lg bg-muted/50">
                <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  CPA
                </div>
                <div className="text-lg font-bold">{totals.cpa ? formatCurrency(totals.cpa) : '-'}</div>
              </div>
              <div className="p-2 rounded-lg bg-muted/50">
                <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  ROAS
                </div>
                <div className="text-lg font-bold">{totals.roas ? `${totals.roas.toFixed(1)}x` : '-'}</div>
              </div>
            </div>

            {/* Top Campaigns Table */}
            <div>
              <div className="text-xs font-medium mb-2 text-muted-foreground">Top Campaigns</div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-[10px] font-medium py-1.5">Campaign</TableHead>
                      <TableHead className="text-[10px] font-medium py-1.5 text-right">Spend</TableHead>
                      <TableHead className="text-[10px] font-medium py-1.5 text-right">Leads</TableHead>
                      <TableHead className="text-[10px] font-medium py-1.5 text-right">CPA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-4">
                          No campaign data
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((campaign, idx) => (
                        <TableRow key={campaign.id || idx} className="hover:bg-muted/30">
                          <TableCell className="text-xs py-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px]">
                                {PLATFORM_CONFIG[campaign.platform]?.icon || '📊'}
                              </span>
                              <span className="truncate max-w-[180px]" title={campaign.name}>
                                {campaign.name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-right font-medium">
                            {formatCurrency(campaign.spend)}
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-right">
                            {formatNumber(campaign.leads)}
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-right">
                            {campaign.leads > 0 
                              ? formatCurrency(campaign.spend / campaign.leads)
                              : '-'
                            }
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Footer - Link to Ads Manager */}
            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-[10px] text-muted-foreground">
                {data.length} of {totals.count || '?'} campaigns shown
              </span>
              <Button
                variant="link"
                size="sm"
                onClick={openAdsManager}
                className="h-auto p-0 text-xs"
                data-testid="view-all-campaigns"
              >
                View all in Ads Manager →
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdsSummaryCard;
